<!-- 工作区顶部信息 -->
<template>
  <div class="workbench">
    <!-- <img
      class="workbench-bg"
      src="@/assets/webp/workbench-bg.webp"
      srcset="@/assets/webp/workbench-bg@2x.webp 2x, @/assets/webp/workbench-bg@3x.webp 3x"
    /> -->
    <div class="workbench-content">
      <header class="workbench-header">
        <!-- 工作区切换 -->
        <div ref="workbenchToggle" class="workbench-header_toggle" @click="onToggleWorkbench">
          <div>{{ currTeamInfo?.name }}</div>
          <IconMyWorkbenchDropDown
            :class="['icon-dropdown', { 'icon-dropdown_up': togglePopupVal }]"
          ></IconMyWorkbenchDropDown>
        </div>

        <!-- 用户拥有2个及以上的工作区才显示此按钮 -->
        <div
          v-if="teamStore.statics && teamStore.statics.user_teams && teamStore.statics.user_teams.length > 1"
          class="workbench-header_allbtn"
          @click="showAllAppFn"
        >
          全部工作区应用
        </div>
      </header>

      <section class="workbench-tag">
        <!--工作区人员 -->
        <div>
          <workbench-info-work-user
            @set-pay-config-to-pay="setPayConfigToPay"
            @init-team-config="getTeamConfig"
          ></workbench-info-work-user>
        </div>

        <!-- 工作区套餐 -->
        <div>
          <workbench-info-pay @set-pay-config-to-pay="setPayConfigToPay"></workbench-info-pay>
        </div>

        <!-- 工作区配额 -->
        <div>
          <workbench-info-config @set-pay-config-to-pay="setPayConfigToPay"></workbench-info-config>
        </div>
      </section>
    </div>
  </div>

  <!-- 支付弹窗 -->
  <workbench-pay-dialog
    v-if="showWorkbenchPay"
    :payConfig="payConfig"
    @on-workbench-pay-dialog-closed="payDialogClose"
    @on-workbench-pay-dialog-success="paySuccess"
  ></workbench-pay-dialog>

  <!-- 全部应用弹窗 -->
  <workbench-all-app-dialog
    v-if="showWorkbenchAllApp"
    @on-workbench-all-app-dialog-closed="showWorkbenchAllApp = false"
  ></workbench-all-app-dialog>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePTeamStore } from '@/store';
import { reqTeamStaticsAsync, resetTeamStaticsAsync } from './workbench-info-interface';
import { getWorkbenchEmitter } from './workbench-info.tools';
import { RespTeam, apiPostTeamCreate, apiPostTeamSwitch } from '@vas2t-lowcode/api';
import { UIPopup } from '@vas2t-lowcode/libs';
import { showMessage } from '@vas2t-lowcode/utils';
import WorkbenchInfoWorkUser from './workbench-info-workuser.vue';
import WorkbenchInfoPay from './workbench-info-pay.vue';
import WorkbenchInfoConfig from './workbench-info-config.vue';
import {
  dwToggleWorkbench,
  dwShowCreateWorkbench,
  dwClickCreateWorkbench,
  dwClickAllWork,
  dwWorkbenchShow,
} from './workbench-info-dw';
import { useRouter } from 'vue-router';
import WorkbenchToggle from './workbench-toggle.vue';
import { appExpireNotice } from '@/utils';

const WorkbenchPayDialog = defineAsyncComponent(() => import('./workbench-pay.vue'));
const WorkbenchAllAppDialog = defineAsyncComponent(() => import('./workbench-all-app-dialog.vue'));

const router = useRouter();
const teamStore = usePTeamStore();

// 显示支付弹窗
const showWorkbenchPay = ref(false);
// 全部应用弹窗
const showWorkbenchAllApp = ref(false);
// 支付配置
const payConfig = ref({});
// 显示埋点是否上报
const dwShow = ref(false);

/** 当前工作区信息 */
const currTeamInfo = computed(() => {
  return teamStore.isShowWorkbench ? teamStore.statics?.curr_team_info : {};
});

/** 获取工作区下拉选项 */
const userTeamsList = computed(() => {
  if (!teamStore.isShowWorkbench) {
    return {};
  }

  const json = teamStore.statics?.user_teams?.reduce(
    (newJson, next) => {
      if (next.is_owner) {
        newJson.selfList?.push(next);
      } else {
        newJson.otherList?.push(next);
      }
      return newJson;
    },
    {
      selfList: [],
      otherList: [],
    } as {
      selfList?: RespTeam[] | undefined;
      otherList?: RespTeam[] | undefined;
    }
  );
  return json;
});

/** 数据是否初始化完成 */
const isDataInit = ref(false);

/** 最大重试次数 */
const MAX_RETRY_TIMES = 5;
/** 当前重试次数 */
let retryGetTeamConfigNum = 0;
/**
 * 获取工作区配置信息
 */
const getTeamConfig = (enableRetry = false) => {
  retryGetTeamConfigNum++;
  let isMyAppListUpdated = false;
  const oldAppListStr = JSON.stringify(teamStore.statics?.team_apps || []);
  return reqTeamStaticsAsync()
    ?.then((rData) => {
      const data = rData?.data || {};

      // 工作区试用/套餐即将到期推送
      if (data.notice?.is_need_notice) {
        appExpireNotice(data, (payConfig: PayConfig | undefined) => {
          setPayConfigToPay(payConfig);
        });
      }

      if (JSON.stringify(data.team_apps || []) !== oldAppListStr) {
        isMyAppListUpdated = true;
      }

      teamStore.setStatics(data);
    })
    .finally(() => {
      teamStore.setIsInitStaticsData(true);
      isDataInit.value = true;
      resetTeamStaticsAsync();

      if (!enableRetry) {
        return;
      }

      if (!isMyAppListUpdated && retryGetTeamConfigNum <= MAX_RETRY_TIMES) {
        // 5次内我的应用还未更新，则重试
        getTeamConfig(enableRetry);
      } else {
        // 不再执行相关数据
        retryGetTeamConfigNum = 0;
      }
    });
};

// 监听是否显示工作区上报显示埋点
watch(
  () => teamStore.isShowWorkbench,
  (val) => {
    if (val && !dwShow.value) {
      dwShow.value = true;
      dwWorkbenchShow();
    }
  },
  { immediate: true }
);

// 获取默认配置信息
getTeamConfig();

// 提供给其他地方使用
onMounted(() => {
  getWorkbenchEmitter().on('get-team-config', getTeamConfig);
});

onUnmounted(() => {
  getWorkbenchEmitter().off('get-team-config', getTeamConfig);
});

/**
 * 设置支付参数并支付
 */
function setPayConfigToPay(config: any) {
  payConfig.value = config;
  showWorkbenchPay.value = true;
}

/**
 * 支付成功
 */
function paySuccess() {
  // 重新获取工作区配置
  getTeamConfig();
}

/**
 * 支付弹窗关闭
 */
function payDialogClose() {
  payConfig.value = {};
  showWorkbenchPay.value = false;
}

/**
 * 显示全部工作区
 */
function showAllAppFn() {
  // 埋点
  dwClickAllWork();
  showWorkbenchAllApp.value = true;
}

/**
 * @description: 工作区切换
 * @return {*}
 */
const workbenchToggle = ref<HTMLElement | null>(null);
const togglePopupVal = ref(false);
const onToggleWorkbench = () => {
  const togglePopup = UIPopup({
    change: (val: boolean) => {
      togglePopupVal.value = val;
      if (val) {
        // 埋点上报
        !userTeamsList.value?.selfList?.length && dwShowCreateWorkbench();
      }
    },
    el: workbenchToggle.value,
    component: WorkbenchToggle,
    params: {
      userTeamsList: userTeamsList.value,
      currTeamInfo: currTeamInfo.value,
      // 工作区选中事件
      onWorkbenchToggle: (item: RespTeam) => {
        if (item.team_id === currTeamInfo.value?.team_id) {
          // 选中当前工作区，无需调用接口
          togglePopup.remove();
          return;
        }
        // 埋点 && api 切换工作区
        dwToggleWorkbench();
        apiPostTeamSwitch({ team_id: item.team_id }).then(() => {
          router.push({
            query: {},
          });
          showMessage({ msgContent: '工作区切换成功', msgType: 'success' });
          // 重新获取配置
          getTeamConfig();
          // 关闭下拉选择工作区
          togglePopup.remove();
        });
      },
      // 创建新的工作区
      onWorkbenchCreate: () => {
        dwClickCreateWorkbench();
        apiPostTeamCreate()
          .then(() => {
            showMessage({ msgContent: '已为您创建工作区，赶紧搭建自己的应用吧！', msgType: 'success' });
            // 重新获取配置
            getTeamConfig();
            togglePopup.remove();
          })
          .catch(() => {
            showMessage({ msgContent: '创建工作区失败，请稍后重试', msgType: 'error' });
            togglePopup.remove();
          });
      },
    },
  });
};
</script>

<style lang="scss" scoped>
.workbench {
  background-image: url('@/assets/webp/workbench-bg.webp');
  background-size: 1650px cover;
  background-repeat: no-repeat;
}

@media (min-width: 1650px) {
  .workbench {
    background-image: url('@/assets/webp/workbench-bg@2x.webp');
    background-size: cover;
  }
}

.workbench {
  color: #fff;
  border-radius: 8px 8px 0 0;
  position: relative;
  overflow: hidden;
  flex: none;
  // height: 136px;
  background-color: #3f7cf6;
  padding-bottom: 10px;

  // 背景图
  // &-bg {
  //   width: 100%;
  //   display: block;
  //   position: absolute;
  //   left: 0;
  //   top: 0;
  //   z-index: 1;
  // }

  &-content {
    position: relative;
    z-index: 2;
  }

  // 顶部切换区
  &-header {
    display: flex;
    justify-content: space-between;
    min-height: 24px;
    padding: 14px 0 18px 24px;

    // 弹出层操作按钮
    &_toggle {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 14px;
      line-height: 22px;
      font-weight: 700;

      .icon-dropdown {
        margin-left: 4px;
      }
      .icon-dropdown_up {
        transform: rotate(180deg);
      }
    }

    // 全部应用按钮
    &_allbtn {
      background: #ffffff;
      color: #417ff9;
      font-size: 12px;
      height: 24px;
      line-height: 24px;
      padding: 0 9px 0 13px;
      border-radius: 12px 0 0 12px;
      cursor: pointer;

      &:hover {
        background: #ecf2fe;
      }

      &:active {
        background: #cfdcfd;
      }
    }
  }

  // 标签显示区
  &-tag {
    display: flex;
    align-items: center;
    padding: 0 50px 25px;
    max-width: 1310px;
    box-sizing: border-box;
    justify-content: space-between;
    > div {
      display: flex;
      align-items: center;
      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>

<style lang="scss">
.el-popover.el-popper.popover-workbench-info {
  box-sizing: border-box;
  width: 184px;
  max-height: 300px;
  overflow: hidden;
  background: var(--lowcode-background-01);
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  border: 1px solid rgba(38, 46, 62, 0.15);
  padding: 4px 2px 0;
}

.workbench-btn {
  background-color: rgba(255, 255, 255, 0.3) !important;
  border: none !important;
  color: #fff !important;
  height: 20px !important;
  min-height: 20px !important;
  font-size: 11px !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.5) !important;
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.6) !important;
  }
}
</style>
